import { get as lodashGet } from "lodash";

import { GROUPING_GRID_NO_GROUPS, NO_GROUPING_QS_VALUE } from "../constants";
import type { IExtendableDto } from "../dto";
import type {
  DtoFieldType,
  IGroupResult,
  PassengerEntity,
  RydeUiUnit,
} from "../models";
import {
  isEntityServerGroupedPagination,
  isEntityServerSidePaginated,
} from "./common.helpers";
import {
  isRideMapDelayStatusGroupingField,
  normalizeLiveMapDelayStatusGroupValue,
} from "./table.helpers";

export type TTableFilteredGroupedRowsMap = Readonly<
  Record<string, readonly IExtendableDto[]>
>;

export const buildTableGroupRowMapKey = (
  groupValue: DtoFieldType | typeof GROUPING_GRID_NO_GROUPS,
): string => {
  if (groupValue === GROUPING_GRID_NO_GROUPS) {
    return GROUPING_GRID_NO_GROUPS;
  }

  return String(groupValue ?? "");
};

const normalizeGroupValueForField = ({
  groupingFieldNameResolved,
  groupValue,
}: {
  groupingFieldNameResolved: string | number | undefined;
  groupValue: unknown;
}): unknown => {
  if (!isRideMapDelayStatusGroupingField(String(groupingFieldNameResolved))) {
    return groupValue;
  }

  return normalizeLiveMapDelayStatusGroupValue(groupValue);
};

export const buildTableFilteredGroupedRowsMap = ({
  filteredRows,
  groupingFieldNameResolved,
}: {
  filteredRows: readonly IExtendableDto[];
  groupingFieldNameResolved: string | number | undefined;
}): TTableFilteredGroupedRowsMap => {
  if (!filteredRows.length) {
    return {};
  }

  if (
    groupingFieldNameResolved === undefined ||
    groupingFieldNameResolved === NO_GROUPING_QS_VALUE
  ) {
    return { [GROUPING_GRID_NO_GROUPS]: filteredRows };
  }

  const groupedRowsMap: Record<string, IExtendableDto[]> = {};

  for (const row of filteredRows) {
    const groupValue = normalizeGroupValueForField({
      groupingFieldNameResolved,
      groupValue: lodashGet(row, groupingFieldNameResolved),
    }) as DtoFieldType;
    const groupKey = buildTableGroupRowMapKey(groupValue);

    if (!groupedRowsMap[groupKey]) {
      groupedRowsMap[groupKey] = [];
    }

    groupedRowsMap[groupKey].push(row);
  }

  return groupedRowsMap;
};

export const getTableFilteredGroupedRowsFromMap = ({
  groupedRowsMap,
  groupingFieldValue,
  groupingFieldNameResolved,
}: {
  groupedRowsMap: TTableFilteredGroupedRowsMap;
  groupingFieldValue: DtoFieldType | typeof GROUPING_GRID_NO_GROUPS;
  groupingFieldNameResolved: string | number | undefined;
}): readonly IExtendableDto[] => {
  if (
    groupingFieldNameResolved === undefined ||
    groupingFieldNameResolved === NO_GROUPING_QS_VALUE ||
    groupingFieldValue === GROUPING_GRID_NO_GROUPS
  ) {
    return groupedRowsMap[GROUPING_GRID_NO_GROUPS] ?? [];
  }

  const normalizedGroupValue = normalizeGroupValueForField({
    groupingFieldNameResolved,
    groupValue: groupingFieldValue,
  }) as DtoFieldType | typeof GROUPING_GRID_NO_GROUPS;

  return groupedRowsMap[buildTableGroupRowMapKey(normalizedGroupValue)] ?? [];
};

const readDtoMetadata = (
  dto: IExtendableDto,
): Record<string, unknown> | undefined => {
  if (!dto.metadata || typeof dto.metadata !== "object") {
    return undefined;
  }

  return dto.metadata as Record<string, unknown>;
};

const mergeSavedDtoIntoGroupedRow = ({
  previous,
  saved,
}: {
  previous: IExtendableDto;
  saved: IExtendableDto;
}): IExtendableDto => {
  const firstName = saved.firstName ?? previous.firstName;
  const lastName = saved.lastName ?? previous.lastName;
  const fullName = [firstName, lastName]
    .filter(value => typeof value === "string" && value.trim())
    .join(" ");
  const previousMetadata = readDtoMetadata(previous);
  const savedMetadata = readDtoMetadata(saved);

  // Grid Full Name is metadata.fullName; a PATCH body often omits it or keeps a stale copy.
  const mergedRow = {
    ...previous,
    ...saved,
    metadata: {
      ...previousMetadata,
      ...savedMetadata,
      ...(fullName ? { fullName } : {}),
    },
  } satisfies Partial<PassengerEntity>;

  return mergedRow;
};

/** Patch a saved DTO into server-paginated group buckets (the flat table atom is empty there). */
export const mergeRowIntoGroupedTableResults = ({
  groups,
  dto,
}: {
  groups: readonly IGroupResult<IExtendableDto[]>[];
  dto: IExtendableDto;
}): IGroupResult<IExtendableDto[]>[] => {
  let didReplace = false;

  const next = groups.map(group => {
    const rows = group.groupData ?? [];
    const index = rows.findIndex(row => row.id === dto.id);

    if (index < 0) {
      return group;
    }

    didReplace = true;

    const nextGroup = {
      ...group,
      groupData: rows.toSpliced(
        index,
        1,
        mergeSavedDtoIntoGroupedRow({ previous: rows[index], saved: dto }),
      ),
    } satisfies IGroupResult<IExtendableDto[]>;

    return nextGroup;
  });

  return didReplace ? next : [...groups];
};

export const resolveIsFilteredRows = ({
  groupValues,
  groupingField,
  filteredRowIdsSize,
  groupedRowsMap,
}: {
  groupValues:
    IExtendableDto[keyof IExtendableDto][] | typeof GROUPING_GRID_NO_GROUPS;
  groupingField?: string;
  filteredRowIdsSize: number;
  groupedRowsMap: TTableFilteredGroupedRowsMap;
}): boolean => {
  if (groupValues === GROUPING_GRID_NO_GROUPS) {
    return filteredRowIdsSize > 0;
  }

  const result = groupValues.flatMap(groupingValue =>
    getTableFilteredGroupedRowsFromMap({
      groupedRowsMap,
      groupingFieldValue: groupingValue,
      groupingFieldNameResolved: groupingField,
    }),
  );

  return result.length > 0;
};

/**
 * Server-grouped pages keyed by their group value. The ungrouped page arrives as `GROUPING_GRID_NO_GROUPS`; a
 * `null` value is the group of blank values, keyed `""` as every lookup reads it.
 */
const buildTableGroupedRowsMapFromServerGroups = (
  serverGroupedRows: readonly IGroupResult<IExtendableDto[]>[],
): TTableFilteredGroupedRowsMap =>
  Object.fromEntries(
    serverGroupedRows.map(({ groupValue, groupData }) => [
      buildTableGroupRowMapKey(groupValue),
      groupData ?? [],
    ]),
  );

/**
 * Picks the rows store that feeds the grid. Only server-GROUPED grids (passengers) keep their rows in the
 * grouped-rows atom, which the fetch hook fills per `groupByValues`; every other grid, server-paginated or
 * not, stores its page in the flat rows atom and reads it through the client pipeline. The sources are
 * thunks so the selector subscribes only to the store it actually reads (Recoil tracks `get` calls).
 */
export const resolveTableFilteredGroupedRowsMap = ({
  entity,
  getServerGroupedRows,
  getClientFilteredRows,
}: {
  entity: RydeUiUnit;
  getServerGroupedRows: () => readonly IGroupResult<IExtendableDto[]>[];
  getClientFilteredRows: () => {
    filteredRows: readonly IExtendableDto[];
    groupingFieldNameResolved: string | number | undefined;
  };
}): TTableFilteredGroupedRowsMap => {
  if (isEntityServerGroupedPagination(entity)) {
    return buildTableGroupedRowsMapFromServerGroups(getServerGroupedRows());
  }

  return buildTableFilteredGroupedRowsMap(getClientFilteredRows());
};

/**
 * A server-paged grid loads one page, so its row count is the server total across every page. "Selected
 * only" narrows a grid to its selected loaded rows, so that mode counts those, except on a server-grouped grid
 * (passengers), which draws its rows without that filter. Thunks keep the selector subscribed to one count.
 */
export const resolveDisplayedRowsCount = ({
  entity,
  shouldShowSelectedOnlyRows,
  getServerRowsCount,
  getLoadedRowsCount,
}: {
  entity: RydeUiUnit;
  shouldShowSelectedOnlyRows: boolean;
  getServerRowsCount: () => number;
  getLoadedRowsCount: () => number;
}): number => {
  const isNarrowedToSelectedRows =
    shouldShowSelectedOnlyRows && !isEntityServerGroupedPagination(entity);

  if (isEntityServerSidePaginated(entity) && !isNarrowedToSelectedRows) {
    return getServerRowsCount();
  }

  return getLoadedRowsCount();
};

/**
 * A server-grouped grid (passengers) loads one page of each group, so a group header shows the group's server
 * count; every other grid groups the rows it has loaded and counts those.
 */
export const resolveGroupRowsCount = ({
  entity,
  serverGroupRowsCount,
  loadedGroupRowsCount,
}: {
  entity: RydeUiUnit;
  serverGroupRowsCount: number;
  loadedGroupRowsCount: number;
}): number =>
  isEntityServerGroupedPagination(entity)
    ? serverGroupRowsCount
    : loadedGroupRowsCount;
